import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API, getCompanyContext } from "../lib/api";
import { accessToken } from "../lib/session";
import { Sparkles, Send, Loader2 } from "lucide-react";

const SUGGESTIONS_COMPANY = ["Quantos funcionários tenho?", "Quem está de férias?", "Quanto minha folha aumentou?", "Quais são minhas principais pendências?", "Quem foi admitido este mês?", "Tem alguma coisa que eu preciso fazer hoje?"];
const SUGGESTIONS_EMP = ["Quando vencem minhas férias?", "Quais benefícios eu tenho?", "Como está minha solicitação?"];

export function AssistantSheet({ open, onOpenChange, employee = false }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(null);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (q) => {
    const text = (q || input).trim();
    if (!text || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken()}` },
        body: JSON.stringify({ message: text, session_id: sid.current, company_id: getCompanyContext() }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.session_id) sid.current = ev.session_id;
          if (ev.delta || ev.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + (ev.delta || ev.error) }; return c; });
        }
      }
    } catch (e) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: e.message || "Não consegui responder agora. Tente novamente." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="ai-assistant-sheet" className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="font-display flex items-center gap-2"><span className="h-8 w-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center"><Sparkles className="h-4 w-4" /></span>DP Assist</SheetTitle>
          <SheetDescription className="text-xs">Responde apenas com dados que você tem permissão para consultar. Diferencia dado de interpretação. Não substitui o cálculo oficial da folha.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {msgs.length === 0 && (
            <div className="space-y-2">
              <p className="text-sm text-slate-500">Pergunte em linguagem simples:</p>
              {(employee ? SUGGESTIONS_EMP : SUGGESTIONS_COMPANY).map((s, i) => (
                <button key={s} data-testid={`ai-suggestion-${i}`} onClick={() => ask(s)} className="block w-full text-left text-sm border rounded-lg px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors">{s}</button>
              ))}
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} data-testid={`ai-message-${m.role}`} className={m.role === "user" ? "ml-10 bg-indigo-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm" : "mr-6 bg-slate-50 border rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed"}>
              {m.content || (busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />)}
            </div>
          ))}
          <div ref={end} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="border-t p-4 flex gap-2">
          <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ex.: Quanto gastei com hora extra nos últimos seis meses?" disabled={busy} />
          <Button data-testid="ai-send-button" type="submit" disabled={busy || !input.trim()} size="icon"><Send className="h-4 w-4" /></Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
